import type { StationRequestRecord } from "../../../services/FormService";

/**
 * Hồ sơ CHUỖI trong màn duyệt — anh Quân chốt 30/08/2026.
 *
 * Chuỗi chỉ khai tên nhóm và một con số ƯỚC TÍNH số điểm; đội WhaleLO gọi điện rồi tự
 * thêm từng điểm ở đây. Mỗi điểm sau này thành một station thật, nên phải đủ thông tin
 * y như một hồ sơ lẻ — thiếu ô nào là điểm đó không lên bán được.
 *
 * Chỗ lưu: `Form.extenalInfo.multiLocation.locations[]`. Dùng lại `PUT /forms/:id` đang
 * chạy, không thêm route mới và không đụng bảng nào.
 */
export interface DiemChuoi {
	ten: string;
	maps: string;
	diaChi: string;
	dienThoai: string;
	gio: string;
	toaDo: string;
	nuoc: string;
	thanhPho: string;
	/** Email đăng nhập của điểm — TUỲ CHỌN. Có thì ký chuỗi xong điểm được sinh tài khoản
	 *  + gửi thư đăng nhập ngay; không thì chủ chuỗi vận hành hộ, bổ sung sau. */
	email: string;
}

export const DIEM_RONG: DiemChuoi = {
	ten: "",
	maps: "",
	diaChi: "",
	dienThoai: "",
	gio: "",
	toaDo: "",
	nuoc: "",
	thanhPho: "",
	email: "",
};

/** Nhãn tiếng Anh của từng ô, kèm tên ngắn để ghép câu "Missing name, phone". */
export const O_DIEM: { khoa: keyof DiemChuoi; nhan: string; nganGon: string; goiY?: string; tuyChon?: boolean }[] = [
	{ khoa: "ten", nhan: "Location name", nganGon: "name" },
	{ khoa: "maps", nhan: "Google Maps link", nganGon: "Maps link", goiY: "https://maps.app.goo.gl/…" },
	{ khoa: "diaChi", nhan: "Address", nganGon: "address" },
	{ khoa: "dienThoai", nhan: "Phone", nganGon: "phone", goiY: "+84 …" },
	{ khoa: "gio", nhan: "Opening hours", nganGon: "hours", goiY: "08:00 – 22:00" },
	{ khoa: "toaDo", nhan: "Coordinates", nganGon: "coordinates", goiY: "16.0678, 108.2208" },
	{ khoa: "nuoc", nhan: "Country", nganGon: "country" },
	{ khoa: "thanhPho", nhan: "City", nganGon: "city" },
	{
		khoa: "email",
		nhan: "Login email (optional)",
		nganGon: "login email",
		goiY: "Leave blank if this location has no email yet",
		tuyChon: true,
	},
];

/** Hồ sơ này có phải chuỗi không. Đọc cả cờ mới lẫn dấu vết cũ để hồ sơ trước 30/08 vẫn nhận ra. */
export function laHoSoChuoi(request?: StationRequestRecord | null): boolean {
	const ext = (request?.extenalInfo || {}) as Record<string, any>;
	if (ext.registrationType === "multiple") return true;
	return Boolean(ext.multiLocation?.chainName || ext.multiLocation?.chainSize);
}

export function tenChuoi(request?: StationRequestRecord | null): string {
	const ext = (request?.extenalInfo || {}) as Record<string, any>;
	return String(ext.multiLocation?.chainName || "").trim();
}

/** Con số họ tự khai — là ước tính, không phải cam kết. */
export function soDiemUocTinh(request?: StationRequestRecord | null): number | null {
	const ext = (request?.extenalInfo || {}) as Record<string, any>;
	const n = Number(ext.multiLocation?.chainSize);
	return Number.isFinite(n) && n > 0 ? n : null;
}

export function danhSachDiem(request?: StationRequestRecord | null): DiemChuoi[] {
	const ext = (request?.extenalInfo || {}) as Record<string, any>;
	const ds = ext.multiLocation?.locations;
	if (!Array.isArray(ds)) return [];
	return ds.map((d: any) => ({ ...DIEM_RONG, ...(d || {}) }));
}

/** Ô nào còn trống — trả tên ngắn để ghép thẳng vào câu nhắc. */
export function diemConThieu(d: DiemChuoi): string[] {
	return O_DIEM.filter((o) => !o.tuyChon && !String(d[o.khoa] || "").trim()).map((o) => o.nganGon);
}

/** Chuỗi chỉ duyệt được khi đã có điểm, và mọi điểm đều đủ thông tin. */
export function chuoiSanSangDuyet(request?: StationRequestRecord | null): {
	san: boolean;
	lyDo: string;
} {
	const ds = danhSachDiem(request);
	if (!ds.length) {
		return { san: false, lyDo: "Add at least one location before approving." };
	}
	const thieu = ds.filter((d) => diemConThieu(d).length).length;
	if (thieu) {
		return {
			san: false,
			lyDo: `${thieu} location(s) still missing details. Each one becomes a real station.`,
		};
	}
	return { san: true, lyDo: "" };
}

/** Ghép `extenalInfo` mới để gửi lên `PUT /forms/:id`, giữ nguyên mọi thứ khác. */
export function ghepExtenalInfoVoiDiem(
	request: StationRequestRecord,
	ds: DiemChuoi[],
): Record<string, any> {
	const ext = (request.extenalInfo || {}) as Record<string, any>;
	return {
		...ext,
		multiLocation: { ...(ext.multiLocation || {}), locations: ds },
	};
}
